import React, { useCallback, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import LayoutFooter from '../layout/LayoutFooter';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { AuthContext } from '../../context/AuthContext';
import { useResponsive } from '../../context/ResponsiveContext';
import { t } from '../../features/walkthrough/i18n/walkthroughI18n';
import { getToursForUser } from '../../features/walkthrough/tours/tourRegistry';
import { useWalkthrough } from '../../features/walkthrough/walkthroughContext';
import {
  queueWalkthroughReplay,
  requestWalkthroughReplay,
  resolveTourStartPath,
} from '../../features/walkthrough/walkthroughOrchestrator';

import { getFooterMeta } from '../../utils/footerMeta';

function InfoPageLayout({ title, children }) {
  const { year, monthYear } = getFooterMeta();
  const { user, isLoading } = useContext(AuthContext);
  const { isWalkthroughEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { startTour, endActiveTour } = useWalkthrough();

  const handleAppTour = useCallback(() => {
    if (!isWalkthroughEnabled || !user || isLoading) return;
    void requestWalkthroughReplay({
      user,
      navigate,
      startTour,
      endActiveTour,
      isMobile,
    });
  }, [endActiveTour, isLoading, isMobile, isWalkthroughEnabled, navigate, startTour, user]);

  return (
    <div className="flex min-h-[100dvh] flex-col overflow-x-hidden bg-gradient-to-br from-blue-50 to-indigo-100 pt-[var(--browser-recommendation-banner-h,0px)] dark:bg-black dark:from-black dark:to-black">
      <header className="border-b border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-black">
        <div className="safe-area-left safe-area-right mx-auto flex max-w-3xl min-w-0 flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <Link
            to="/"
            className="min-w-0 truncate text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-white dark:hover:text-gray-200"
          >
            Kadel Labs Timesheet
          </Link>
          <Button asChild variant="outline" size="sm" className="w-full shrink-0 sm:w-auto">
            <Link to="/">Back to sign in</Link>
          </Button>
        </div>
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        className="safe-area-left safe-area-right mx-auto min-w-0 max-w-3xl px-3 py-6 sm:px-6 sm:py-8"
      >
        <article className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-ds-md">
          <h1 className="mb-4 page-title text-gray-900 dark:text-white">{title}</h1>
          <div className="space-y-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300 sm:text-base">
            {children}
          </div>
        </article>
      </main>
      <div className="mt-auto">
        <LayoutFooter
          year={year}
          monthYear={monthYear}
          onAppTour={isWalkthroughEnabled && user && !isLoading ? handleAppTour : undefined}
        />
      </div>
    </div>
  );
}

export function HelpPage() {
  const { user, isLoading } = useContext(AuthContext);
  const { isWalkthroughEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const tourChapters = useMemo(
    () => (user && isWalkthroughEnabled ? getToursForUser(user) : []),
    [isWalkthroughEnabled, user]
  );

  const handleStartTourChapter = (tour) => {
    queueWalkthroughReplay(tour.id);
    navigate(resolveTourStartPath(user, tour));
  };

  return (
    <InfoPageLayout title="Help">
      <p>
        The Kadel Labs Timesheet application helps you record project hours, submit timesheets for approval,
        and review status updates from your project manager.
      </p>
      <p>
        Sign in with your Microsoft work account from the home page. If you cannot access the application,
        contact your administrator or IT support team.
      </p>
      <p>
        For timesheet entry questions (projects, holidays, leave), refer to your team&apos;s internal
        time-tracking guidelines or reach out to your reporting manager.
      </p>

      {isWalkthroughEnabled && !isLoading && user && tourChapters.length > 0 ? (
        <section className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
          <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
            {t('walkthrough.help.interactiveTourTitle')}
          </h2>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {t('walkthrough.help.interactiveTourBody')}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {tourChapters.map((tour) => (
              <Button
                key={tour.id}
                type="button"
                variant="outline"
                size="sm"
                className="justify-start"
                onClick={() => handleStartTourChapter(tour)}
              >
                {tour.title}
              </Button>
            ))}
          </div>
        </section>
      ) : null}
    </InfoPageLayout>
  );
}

export function PrivacyPage() {
  return (
    <InfoPageLayout title="Privacy Policy">
      <p>
        This application is provided for Kadel Labs workforce time tracking. Authentication is handled
        through Microsoft Office 365; we do not store your Microsoft password.
      </p>
      <p>
        Timesheet data (hours, projects, approvals, and related metadata) is stored in company-controlled
        systems and used for payroll, project reporting, and operational purposes consistent with
        organizational policies.
      </p>
      <p>
        Access is limited by role-based permissions. For privacy requests or data questions, contact your
        HR or IT administrator.
      </p>
    </InfoPageLayout>
  );
}

export function TermsPage() {
  return (
    <InfoPageLayout title="Terms of Use">
      <p>
        By signing in, you agree to use this application only for authorized business purposes and to
        submit accurate time records.
      </p>
      <p>
        You are responsible for reviewing your entries before submission and for complying with company
        policies on timesheet deadlines and approvals.
      </p>
      <p>
        Misuse of the system or falsification of time records may result in disciplinary action under
        applicable employment policies.
      </p>
    </InfoPageLayout>
  );
}
